import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, ReactNode } from 'react';
import { useRef } from 'react';
import styles from './HeaderShowrealLarge.module.scss';
import { scrollOutTransition, setupTransitionInTimeline } from './HeaderShowrealLarge.transitions';
import type { HeaderShowrealLargeProps } from './HeaderShowrealLarge.data';
import { useScrollTransition } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import { NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import PlayButton from '../../atoms/play-button/PlayButton';
import Video from '../../atoms/video/Video';
import { useFullScreen } from './useFullScreen';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../hooks/useSetupTimeline';

export default function HeaderShowrealLarge({
  canTransitionIn,
  blok,
  ref,
  ...props
}: HeaderShowrealLargeProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const {
    isFullScreen,
    toggleFullScreen,
    onPlayClick,
    topTitleRef,
    maskTopTitleRef,
    maskBottomTitleRef,
    bottomTitleRef,
    maskedContentRef,
    previewContainerRef,
    videoContainerRef,
    videoRef,
  } = useFullScreen(blok, elementRef);

  const { inView } = useScrollTransition({
    scroll: {
      canTransitionIn,
      trigger: elementRef,
      vars: {},
      enableInView: true,
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        maskTopTitleRef,
        maskBottomTitleRef,
        bottomTitleRef,
        topTitleRef,
        previewContainerRef,
      },
      setupTransitionInTimeline,
    }),
  });

  useScrollTrigger({
    canTransitionIn,
    trigger: elementRef,
    endTrigger: topTitleRef,
    animation: useSetupTimeline(scrollOutTransition, {
      title: maskTopTitleRef,
      maskTitle: topTitleRef,
    }),
    vars: {
      scrub: true,
      start: 'top top',
      end: 'bottom top',
    },
  });

  useScrollTrigger({
    canTransitionIn,
    trigger: bottomTitleRef,
    endTrigger: bottomTitleRef,
    animation: useSetupTimeline(scrollOutTransition, {
      title: maskBottomTitleRef,
      maskTitle: bottomTitleRef,
    }),
    vars: {
      scrub: true,
      start: 'top bottom',
      end: 'bottom top',
    },
  });

  return (
    <div
      ref={elementRef}
      className={classNames(
        styles.headerShowrealLarge,
        props.className,
        styles[`top-title-size-${blok.title_top.content.length}`],
        styles[`bottom-title-size-${blok.title_bottom.content.length}`],
      )}
      {...sbEditable(blok)}
    >
      <div className={classNames(styles.content)}>
        <div className={classNames('grid-gutter max-content-width')}>
          <Heading
            ref={topTitleRef}
            as="h1"
            size="heading-01"
            className={classNames(styles.topTitle)}
            disableTriggerInOnScroll
          >
            {render(blok.title_top, {
              nodeResolvers: {
                [NODE_PARAGRAPH]: (children: ReactNode) => (
                  <>
                    {children}
                    <br />
                  </>
                ),
              },
            })}
          </Heading>

          <div className={classNames(styles.previewContainer)} />
        </div>

        <div ref={maskedContentRef} className={classNames(styles.maskedContent)}>
          <div className={classNames('grid-gutter max-content-width')}>
            <Heading
              ref={maskTopTitleRef}
              as="h1"
              size="heading-01"
              className={classNames(styles.topTitle, 'text-outline')}
              disableTriggerInOnScroll
            >
              {render(blok.title_top, {
                nodeResolvers: {
                  [NODE_PARAGRAPH]: (children: ReactNode) => (
                    <>
                      {children}
                      <br />
                    </>
                  ),
                },
              })}
            </Heading>

            <div ref={previewContainerRef} className={classNames(styles.previewContainer)}>
              <div ref={videoContainerRef} className={classNames(styles.videoContainer)}>
                <Video
                  ref={videoRef}
                  className={classNames(styles.video, 'abs-fill')}
                  blok={blok.video[0]}
                  playing={inView}
                  playsinline
                  controls
                  removeOnPause={false}
                  openInModal={false}
                  innerTransition={{
                    type: 'scaleUp',
                  }}
                  controlsVisible={isFullScreen}
                  loop
                  isFullScreen={isFullScreen} // isFullScreen && bowser.mobile
                  muted={!isFullScreen}
                  exitFullScreen={toggleFullScreen}
                />

                <PlayButton
                  className={classNames(styles.playButton, 'abs-center')}
                  isPlaying={isFullScreen}
                  isVisible={{
                    onMount: true,
                    value: !isFullScreen,
                  }}
                  onClick={onPlayClick}
                />
              </div>
            </div>

            <Heading
              ref={maskBottomTitleRef}
              as="h1"
              size="heading-01"
              className={classNames(styles.bottomTitle, 'text-outline')}
              disableTriggerInOnScroll
            >
              {render(blok.title_bottom, {
                nodeResolvers: {
                  [NODE_PARAGRAPH]: (children: ReactNode) => (
                    <>
                      {children}
                      <br />
                    </>
                  ),
                },
              })}
            </Heading>
          </div>
        </div>

        <div className={classNames('grid-gutter max-content-width')}>
          <Heading
            ref={bottomTitleRef}
            as="h1"
            size="heading-01"
            className={classNames(styles.bottomTitle)}
            disableTriggerInOnScroll
          >
            {render(blok.title_bottom, {
              nodeResolvers: {
                [NODE_PARAGRAPH]: (children: ReactNode) => (
                  <>
                    {children}
                    <br />
                  </>
                ),
              },
            })}
          </Heading>
        </div>
      </div>
    </div>
  );
}
